export type PendingCategoryKey =
  | 'newItems'
  | 'pendingInitReopenBonds'
  | 'pendingDiscountNotes'
  | 'pendingSwaps'
  | 'pendingRepurcBonds'
  | 'pendingBondDefinitions'
  | 'pendingBackOffices'
  | 'noVerifiedBonds';

export type PendingLinkTarget =
  | { kind: 'bond'; id: string }
  | { kind: 'swap'; id: string }
  | { kind: 'bondDefinition'; id: string }
  | { kind: 'none' };

/** Fields shared by every pending item returned by `GET /PendingItem`. */
export interface PendingItemBase {
  tradeId: string;
  dealId: string;
  tradeDate: string;
  doneUser: string;
}

export interface PendingNewItemRow extends PendingItemBase {
  securityId: string;
  securityType: string;
  tradeType: string;
}

export interface PendingInitReopenBondRow extends PendingItemBase {
  securityId: string;
  type: string;
  proceeds: string;
  documentation: string;
  fndCost: string;
  investorAllocation: string;
  leads: string;
  arbitrage: string;
}

export interface PendingDiscountNoteRow extends PendingItemBase {
  securityId: string;
  documentation: string;
  investorAllocation: string;
}

export interface PendingSwapRow extends PendingItemBase {
  structureId: string;
  rfrTradeId: string;
  purpose: string;
  purposeSubtype: string;
}

export interface PendingRepurcBondRow extends PendingItemBase {
  securityId: string;
  repurch: string;
  refinance: string;
  spread: string;
}

export interface PendingBondDefinitionRow extends PendingItemBase {
  securityId: string;
  documentation: string;
  proceeds: string;
  format: string;
}

export interface PendingBackOfficeRow extends PendingItemBase {
  securityId: string;
  type: string;
  bondPricingSupplement: string;
  tranche: string;
  hasLegalDocument: string;
  hasTermSheet: string;
  hasRta: string;
}

export interface PendingNoVerifiedBondRow extends PendingItemBase {
  securityId: string;
  securityType: string;
  tradeType: string;
}

export type PendingAnyRow =
  | PendingNewItemRow
  | PendingInitReopenBondRow
  | PendingDiscountNoteRow
  | PendingSwapRow
  | PendingRepurcBondRow
  | PendingBondDefinitionRow
  | PendingBackOfficeRow
  | PendingNoVerifiedBondRow;

export interface PendingItemsModel {
  alertDate: string;
  newItems: PendingNewItemRow[];
  pendingInitReopenBonds: PendingInitReopenBondRow[];
  pendingDiscountNotes: PendingDiscountNoteRow[];
  pendingSwaps: PendingSwapRow[];
  pendingRepurcBonds: PendingRepurcBondRow[];
  pendingBondDefinitions: PendingBondDefinitionRow[];
  pendingBackOffices: PendingBackOfficeRow[];
  noVerifiedBonds: PendingNoVerifiedBondRow[];
}

export interface PendingCategoryDescriptor {
  key: PendingCategoryKey;
  label: string;
  description: string;
  icon: string;
  datatableId: string;
  columnCount: number;
}

export interface PendingCategoryView extends PendingCategoryDescriptor {
  count: number;
}

export const EMPTY_PENDING_ITEMS: PendingItemsModel = {
  alertDate: '',
  newItems: [],
  pendingInitReopenBonds: [],
  pendingDiscountNotes: [],
  pendingSwaps: [],
  pendingRepurcBonds: [],
  pendingBondDefinitions: [],
  pendingBackOffices: [],
  noVerifiedBonds: [],
};

export const PENDING_CATEGORIES: PendingCategoryDescriptor[] = [
  {
    key: 'newItems',
    label: 'New Securities',
    description: 'Recently added securities with no business information',
    icon: 'ki-filled ki-abstract-26',
    datatableId: 'kt_dashboard_new_items_datatable',
    columnCount: 6,
  },
  {
    key: 'pendingInitReopenBonds',
    label: 'Bonds',
    description: 'Partial business information missing in Bonds',
    icon: 'ki-filled ki-bank',
    datatableId: 'kt_dashboard_bonds_datatable',
    columnCount: 10,
  },
  {
    key: 'pendingDiscountNotes',
    label: 'Discount Notes',
    description: 'Partial business information missing in Discount Notes',
    icon: 'ki-filled ki-note-2',
    datatableId: 'kt_dashboard_discount_notes_datatable',
    columnCount: 6,
  },
  {
    key: 'pendingSwaps',
    label: 'Swaps',
    description: 'Partial business information missing in Swaps',
    icon: 'ki-filled ki-arrow-right-left',
    datatableId: 'kt_dashboard_swaps_datatable',
    columnCount: 8,
  },
  {
    key: 'pendingRepurcBonds',
    label: 'Repurchases',
    description: 'Partial business information missing in Repurchases',
    icon: 'ki-filled ki-arrow-circle-left',
    datatableId: 'kt_dashboard_repurchases_datatable',
    columnCount: 8,
  },
  {
    key: 'pendingBondDefinitions',
    label: 'Bond Definitions',
    description: 'Partial business information missing in Bond Definitions',
    icon: 'ki-filled ki-document',
    datatableId: 'kt_dashboard_bond_definitions_datatable',
    columnCount: 7,
  },
  {
    key: 'pendingBackOffices',
    label: 'Back Office',
    description: 'Back office pending items',
    icon: 'ki-filled ki-briefcase',
    datatableId: 'kt_dashboard_back_office_datatable',
    columnCount: 10,
  },
  {
    key: 'noVerifiedBonds',
    label: 'Canceled Bonds',
    description: 'Canceled bonds in FRIDA',
    icon: 'ki-filled ki-cross-circle',
    datatableId: 'kt_dashboard_canceled_bonds_datatable',
    columnCount: 6,
  },
];
